import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { formatRateFraction } from "@bw/shared/types";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  ContractTermsResponse,
  DefaultContractTermsPublicationResult,
  DefaultContractTermsVersionResponse,
  PublishDefaultContractTermsRequest,
} from "@bw/shared/types/generated";
import { formatDateUtc } from "@bw/shared/utils";
import { PlatformPage } from "@/components/PlatformPage";
import { getDefaultContractTerms, publishDefaultContractTerms } from "../api";
import { ContractTermsEditor } from "../components/ContractTermsEditor";
import {
  platformServicesAgreementDraftFields,
  platformServicesAgreementDraftMismatch,
} from "../platformServicesAgreementDraft";
import {
  buildContractVersionRequest,
  createContractTermsDraft,
  type ContractTermsDraft,
} from "../contractTermsDraft";

const GRANDFATHER_DAYS = 60;

/** The shared editor prefills from contract terms; map the default onto that shape. */
function asEditorSource(
  version: DefaultContractTermsVersionResponse | null | undefined,
): ContractTermsResponse | null {
  if (!version) return null;
  const publication = version.document_publication;
  return {
    commission_rate: version.commission_rate,
    payout_schedule: version.payout_schedule,
    effective_date: version.effective_date,
    service_fee_bindings: version.service_fee_bindings,
    document: publication
      ? { ...publication.document, approval_reference: publication.approval_reference }
      : null,
  } as unknown as ContractTermsResponse;
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ fontWeight: 600, m: 0, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
}

function payoutLabel(version: DefaultContractTermsVersionResponse): string {
  const schedule = version.payout_schedule;
  return `${schedule.frequency} · ${schedule.timezone ?? "UTC"}`;
}

function CurrentDefault({
  version,
  followers,
}: {
  version: DefaultContractTermsVersionResponse;
  followers: number;
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          Default terms v{version.version_number}
        </Typography>
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gap: 2.5,
            m: 0,
            mt: 2.5,
          }}
        >
          <Fact label="Commission" value={formatRateFraction(version.commission_rate)} />
          <Fact label="Payout" value={payoutLabel(version)} />
          <Fact label="Effective" value={formatDateUtc(version.effective_date)} />
          <Fact label="Businesses on the default" value={String(followers)} />
          <Fact
            label="Agreement"
            value={version.has_document ? "Reviewed document attached" : "No document"}
          />
          <Fact label="Published" value={formatDateUtc(version.published_at)} />
        </Box>
      </CardContent>
    </Card>
  );
}

function History({ history }: { history: readonly DefaultContractTermsVersionResponse[] }) {
  if (history.length === 0) return null;
  return (
    <Card variant="outlined" id="default-terms-history">
      <CardContent>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          History
        </Typography>
        <Table size="small" aria-label="Default terms history">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell>Commission</TableCell>
              <TableCell>Effective</TableCell>
              <TableCell>Published</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((version) => (
              <TableRow key={version.version_id}>
                <TableCell>v{version.version_number}</TableCell>
                <TableCell>{formatRateFraction(version.commission_rate)}</TableCell>
                <TableCell>{formatDateUtc(version.effective_date)}</TableCell>
                <TableCell>{formatDateUtc(version.published_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * Universal default commercial terms. Every business follows these unless a
 * platform admin publishes custom terms for it. Publishing a new default issues
 * it to every following business; their previous terms stay in force for the
 * grandfather window while owners review and accept the new version.
 */
export function DefaultContractTermsPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const today = useRef(new Date().toISOString().slice(0, 10));
  const [pending, setPending] = useState<PublishDefaultContractTermsRequest | null>(null);
  const [editorEpoch, setEditorEpoch] = useState(0);
  const [published, setPublished] = useState<DefaultContractTermsPublicationResult | null>(null);

  const query = useQuery({
    queryKey: ["platform", "contract-defaults", user?.uid],
    queryFn: getDefaultContractTerms,
  });
  const publish = useMutation({
    mutationFn: publishDefaultContractTerms,
    retry: false,
    onError: () => setPending(null),
    onSuccess: async (result) => {
      setPublished(result);
      setPending(null);
      setEditorEpoch((epoch) => epoch + 1);
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-defaults"] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-terms"] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "contract-versions"] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "onboarding"] }),
        queryClient.invalidateQueries({ queryKey: ["platform", "orgs"] }),
      ]);
    },
  });

  const state = query.data;
  const current = state?.current ?? null;
  const followers = state?.following_org_count ?? 0;

  function submit(draft: ContractTermsDraft) {
    const request = buildContractVersionRequest(draft);
    if (!request || publish.isPending) return;
    setPublished(null);
    setPending(request);
  }

  const failureMessage = !publish.error
    ? undefined
    : isApiError(publish.error) && publish.error.status === 409
      ? "Default terms need an effective date from today onward and no earlier than the current default. Your draft is still here."
      : isApiError(publish.error) && publish.error.status === 403
        ? "Your platform admin access couldn’t be confirmed. Your draft is still here."
        : "Default terms weren’t published. Your draft is still here; try again.";
  const pendingIsFuture = pending !== null && pending.effective_date > today.current;

  return (
    <PlatformPage
      title="Platform terms"
      description="Default commercial terms every business receives at registration. Businesses with custom terms keep theirs."
      isLoading={query.isLoading}
      isError={query.isError}
    >
      {state ? (
        <Stack spacing={3}>
          {published ? (
            <Alert severity="success" role="status">
              Published default terms v{published.version.version_number}. Issued to{" "}
              {published.issued_org_ids.length}{" "}
              {published.issued_org_ids.length === 1 ? "business" : "businesses"}
              {published.skipped_org_ids.length > 0
                ? `; ${published.skipped_org_ids.length} skipped (details in the audit log)`
                : ""}
              .
            </Alert>
          ) : null}
          {current ? (
            <CurrentDefault version={current} followers={followers} />
          ) : (
            <Alert severity="warning">
              No default terms yet. Businesses can’t finish onboarding until terms exist — publish
              the default below and every business that is waiting receives it right away.
            </Alert>
          )}
          <Box>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              {current ? "Publish new default terms" : "Publish default terms"}
            </Typography>
            <ContractTermsEditor
              key={editorEpoch}
              defaultValues={createContractTermsDraft(
                asEditorSource(current),
                today.current,
                current?.effective_date ?? null,
              )}
              isPending={publish.isPending}
              publishFailed={publish.isError}
              publishFailureMessage={failureMessage}
              onSubmit={submit}
              showFeeBindings={false}
              documentDraft={{
                label: "Start from the Platform Services Agreement draft",
                fill: (values) => platformServicesAgreementDraftFields(values, `default-v${(current?.version_number ?? 0) + 1}`),
                mismatch: platformServicesAgreementDraftMismatch,
              }}
              onViewHistory={() =>
                document.getElementById("default-terms-history")?.scrollIntoView({ behavior: "smooth" })
              }
            />
          </Box>
          <History history={state.history} />
        </Stack>
      ) : null}

      <Dialog open={pending !== null} onClose={() => (publish.isPending ? undefined : setPending(null))}>
        <DialogTitle>Publish default terms?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {followers === 0
              ? "New businesses will receive these terms when they register."
              : `${followers} ${followers === 1 ? "business follows" : "businesses follow"} the default and will receive these terms. ` +
                (current
                  ? `Their current terms stay in force for ${GRANDFATHER_DAYS} days after the effective date while owners review and accept.`
                  : pendingIsFuture
                    ? `Owners can review and accept them from ${pending?.effective_date}.`
                    : "Owners can review and accept them right away.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={publish.isPending} onClick={() => setPending(null)}>Keep editing</Button>
          <Button
            variant="contained"
            disabled={publish.isPending}
            onClick={() => pending && publish.mutate(pending)}
          >
            {publish.isPending ? "Publishing…" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
    </PlatformPage>
  );
}
